import { BCMSDate, BCMSCurrency } from "@bcm-dev/bcms-vite";
import { Account, PositivePayCheck } from "model";
import TASPositivePayViewChecksSearch from "../TASPositivePayViewChecksSearch";

/**
 * @class TASPositivePayViewChecksSearchUI
 * @classdesc Class definition for controlling the properties that are set in a search feature.
 * 
 * @property {STRING} _accountID - The account id to filter by.
 */
export default class TASPositivePayViewChecksSearchUI {
    constructor(record) {
        this.search = record.search;
        this.didSearch = record.didSearch;
        this.didSaveChanges = record.didSaveChanges;
        this.mobile = record.mobile;
        this.account = record.account;
        this.status = record.status;
        this.issuedDateFrom = record.issuedDateFrom;
        this.issuedDateTo = record.issuedDateTo;
        this.cancelModal = record.cancelModal;
        this.saveAndCancelButtons = record.saveAndCancelButtons;
    }

    /**
     * @name search
     * @instance
     * @property set {STRING} - sets the search value of the class.
     * @memberof TASPositivePayCheckExceptionsSearchUI
     */
    set search(value) { this._search = value; }

    /**
     * @name didSearch
     * @instance
     * @property set {BOOLEAN} - sets whether a search has been performed.
     * @memberof TASPositivePayCheckExceptionsSearchUI
     */
    set didSearch(val) {
        this._didSearch = val;
    }

    /**
     * @name account
     * @instance
     * @property set {STRING} - sets the selected account.
     * @memberof TASPositivePayCheckExceptionsSearchUI
     */
    set account(val) {
        this._account = val;
    }

    /**
     * @name status
     * @instance
     * @property set {STRING} - sets the status.
     * @memberof TASPositivePayCheckExceptionsSearchUI
     */
    set status(val) {
        this._status = val;
    }

    /**
     * @name issuedDateFrom
     * @instance
     * @property set {STRING} - sets the beginning issued date.
     * @memberof TASPositivePayCheckExceptionsSearchUI
     */
    set issuedDateFrom(val) {
        this._issuedDateFrom = val;
    }

    /**
     * @name issuedDateTo
     * @instance
     * @property set {STRING} - sets the ending issued date.
     * @memberof TASPositivePayCheckExceptionsSearchUI
     */
    set issuedDateTo(val) {
        this._issuedDateTo = val;
    }

    /**
     * @name mobile
     * @instance
     * @property set {BOOLEAN} - sets whether the mobile view is open or closed.
     * @memberof TASPositivePayCheckExceptionsSearchUI
     */
    set mobile(val) {
        this._mobile = val;
    }

    /**
     * @name didSaveChanges
     * @instance
     * @property set {BOOLEAN} - sets whether changes have been saved.
     * @memberof TASPositivePayCheckExceptionsSearchUI
     */
    set didSaveChanges(val) {
        this._didSaveChanges = val;
    }

    /**
     * @name cancelModal
     * @instance
     * @property set {BOOLEAN} - sets whether the cancel modal is displayed.
     * @memberof TASPositivePayCheckExceptionsSearchUI
     */
    set cancelModal(val) {
        this._cancelModal = val;
    }

    /**
     * @name saveAndCancelButtons
     * @instance
     * @property set {BOOLEAN} - sets the save and cancel button controls.
     * @memberof TASPositivePayCheckExceptionsSearchUI
     */
    set saveAndCancelButtons(val) {
        this._saveAndCancelButtons = val;
    }

    get search() { return this._search; }

    get didSearch() { return this._didSearch; }

    /**
     * @name account
     * @instance
     * @property get {STRING} - returns the account of the class.
     * @memberof TASPositivePayCheckExceptionsSearch
     */
    get account() {
        return this._account;
    }

    /**
     * @name status
     * @instance
     * @property get {STRING} - returns the status of the class.
     * @memberof TASPositivePayCheckExceptionsSearch
     */
    get status() {
        return this._status;
    }

    /**
     * @name issuedDateFrom
     * @instance
     * @property get {STRING} - returns the beginning issued date of the class.
     * @memberof TASPositivePayCheckExceptionsSearch
     */
    get issuedDateFrom() {
        return this._issuedDateFrom;
    }

    /**
     * @name issuedDateTo
     * @instance
     * @property get {STRING} - returns the ending issued date of the class.
     * @memberof TASPositivePayCheckExceptionsSearch
     */
    get issuedDateTo() {
        return this._issuedDateTo;
    }

    /**
     * @name mobile
     * @instance
     * @property get {BOOLEAN} - returns whether the mobile view is open or closed.
     * @memberof TASPositivePayCheckExceptionsSearch
     */
    get mobile() {
        return this._mobile;
    }

    /**
     * @name didSaveChanges
     * @instance
     * @property get {BOOLEAN} - returns whether changes have been saved.
     * @memberof TASPositivePayCheckExceptionsSearch
     */
    get didSaveChanges() {
        return this._didSaveChanges;
    }

    /**
     * @name cancelModal
     * @instance
     * @property get {BOOLEAN} - returns whether the cancel modal is displayed.
     * @memberof TASPositivePayCheckExceptionsSearch
     */
    get cancelModal() {
        return this._cancelModal;
    }

    /**
     * @name saveAndCancelButtons
     * @instance
     * @property get {BOOLEAN} - returns the save and cancel button controls.
     * @memberof TASPositivePayCheckExceptionsSearch
     */
    get saveAndCancelButtons() {
        return this._saveAndCancelButtons;
    }
    
    /**
     * @name record
     * @instance
     * @property get {OBJECT} - returns the set values
     * @memberof TASPositivePayViewChecksSearchUI
     */
    get record() {
        return {
            search: this.search,
            didSearch: this.didSearch,
            didSaveChanges: this.didSaveChanges,
            mobile: this.mobile,
            account: this.account,
            status: this.status,
            issuedDateFrom: this.issuedDateFrom,
            issuedDateTo: this.issuedDateTo,
            cancelModal: this.cancelModal,
            saveAndCancelButtons: this.saveAndCancelButtons
        };
    }

    static defaultVals() {
        return { 
            search: TASPositivePayViewChecksSearch.init(),
            didSearch: { isActive: false },
            didSaveChanges: { isActive: false },
            mobile: { isActive: true },
            account: { isActive: false },
            status: { isActive: false },
            issuedDateFrom: { isActive: false },
            issuedDateTo: { isActive: false },
            cancelModal: { isActive: false },
            saveAndCancelButtons: { isActive: false }
        };
    }

    reset() {
        const defaultVals = TASPositivePayViewChecksSearchUI.defaultVals();
        return TASPositivePayViewChecksSearchUI.init(defaultVals);
    }
    
    static init(record) {
        const defaultVals = TASPositivePayViewChecksSearchUI.defaultVals();
        const vals = { 
            ...defaultVals,
            ...record ?? {} 
        };

        return new TASPositivePayViewChecksSearchUI(vals);
    }
}